// StatusChip — the single status pill for the whole app (handoff §2).
// One component covers every status domain; switch on `domain` -> tone.
// Renders an inline pill using CSS-variable tokens so it reacts to light/dark
// automatically. Always renders a text label (never colour alone) for a11y.
//
//   <StatusChip domain="inspection" value="PASS" dot />
//   <StatusChip domain="sync" value="QUEUED" />
//   <StatusChip domain="job" value="IN_PROGRESS" />

import React from 'react';

// tone -> {color token, background token}
const TONES = {
  pass: { color: 'var(--pass)', bg: 'var(--pass-bg)' },
  fail: { color: 'var(--fail)', bg: 'var(--fail-bg)' },
  warn: { color: 'var(--warn)', bg: 'var(--warn-bg)' },
  info: { color: 'var(--info)', bg: 'var(--info-bg)' },
  neutral: { color: 'var(--neutral)', bg: 'var(--neutral-bg)' },
  primary: { color: 'var(--primary)', bg: 'var(--primary-tint)' },
};

const norm = (v) => String(v ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');

// domain -> (normalisedValue -> tone)
const DOMAIN_TONE = {
  inspection: { PASS: 'pass', FAIL: 'fail', NTI: 'warn', N_A: 'neutral', NA: 'neutral' },
  defect: { LOW: 'neutral', MEDIUM: 'warn', HIGH: 'fail', OPEN: 'warn', COMPLETED: 'pass' },
  reorder: { REORDER: 'warn', TRUE: 'warn', YES: 'warn' },
  sync: {
    SYNCED: 'pass',
    QUEUED: 'info',
    SYNCING: 'info',
    FAILED: 'fail',
    NOT_SYNCED: 'neutral',
    LEGACY: 'neutral',
  },
  job: {
    WIP: 'info',
    IN_TRANSIT: 'neutral',
    TEARDOWN: 'info',
    TEARDOWN_COMPLETE: 'info',
    QUOTE_SENT: 'warn',
    IN_PROGRESS: 'primary',
    COMPLETE: 'pass',
    CANCELLED: 'neutral',
  },
  approval: { PENDING: 'warn', APPROVED: 'pass', DENIED: 'fail', REJECTED: 'fail', NOT_APPROVED: 'fail' },
  visit: { PLANNED: 'info', IN_PROGRESS: 'primary', COMPLETED: 'pass', CANCELLED: 'neutral' },
  certificate: { STALE: 'warn', OUTDATED: 'warn', READY: 'pass', FRESH: 'pass' },
};

// Values shown verbatim in upper-case; everything else gets Title Case humanising.
const KEEP_UPPER = new Set(['PASS', 'FAIL', 'NTI', 'N_A', 'NA', 'REORDER', 'SYNCED', 'QUEUED', 'FAILED', 'PENDING', 'APPROVED', 'DENIED', 'WIP']);

function humanLabel(value) {
  const n = norm(value);
  if (n === 'N_A' || n === 'NA') return 'N/A';
  if (KEEP_UPPER.has(n)) return n.replace(/_/g, ' ');
  // Title-case the rest: IN_PROGRESS -> "In progress"
  const words = n.toLowerCase().split('_');
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

export default function StatusChip({
  value,
  domain = 'inspection',
  label,
  dot = false,
  size = 'sm', // 'sm' | 'md'
  style,
  ...rest
}) {
  if (value === null || value === undefined || value === '') return null;

  const n = norm(value);
  const tone = (DOMAIN_TONE[domain] && DOMAIN_TONE[domain][n]) || 'neutral';
  const { color, bg } = TONES[tone] || TONES.neutral;
  const text = label != null ? label : humanLabel(value);
  const cancelled = domain === 'job' && n === 'CANCELLED';

  const pad = size === 'md' ? '5px 11px' : '3px 9px';
  const fontSize = size === 'md' ? 11 : 9.5;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        color,
        background: bg,
        padding: pad,
        borderRadius: 'var(--r-pill)',
        fontSize,
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '.2px',
        whiteSpace: 'nowrap',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.8 : 1,
        ...style,
      }}
      {...rest}
    >
      {dot && <span style={{ fontSize: '0.85em', lineHeight: 1 }}>●</span>}
      {text}
    </span>
  );
}
